import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SeriesReportDownloadButton from '@/pages/report-manage/report-list/components/SeriesReportDownloadButton';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useEffect, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { ExerciseColor, OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import AppImageView from '@/common/components/form-view/AppImageView';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { MeditationSinglePageFilter } from '@/api/DTO/exercise/meditation.interface';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { GridColDef } from '@mui/x-data-grid';
import {
	useDeleteMajorMutation,
	useGetMajorDetailQuery,
	useGetSingleListQuery,
	useUpdateMajorStatusMutation,
} from '@/api/exercise/majorApiSlice';
import MajorInfoForm from './components/MajorInfoForm';
import { AdvanceSeriesSinglePageResDTO } from '@/api/DTO/exercise/major.interface';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import SetSingleStatusDialog from './components/SetSingleStatusDialog';
import SetSingleOrderDialog from './components/SetSingleOrderDialog';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function MajorDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteMajor] = useDeleteMajorMutation();
	const { majorId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [updateMajor] = useUpdateMajorStatusMutation();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const statusList = [
		{ id: OnoffType.off, name: t('day21.off') },
		{ id: OnoffType.on, name: t('day21.on') },
	];

	const colorList = [
		{ id: ExerciseColor.AQUA, name: t('day21.AQUA') },
		{ id: ExerciseColor.ORANGE, name: t('day21.ORANGE') },
		{ id: ExerciseColor.CORAL, name: t('day21.CORAL') },
		{ id: ExerciseColor.PINK, name: t('day21.PINK') },
		{ id: ExerciseColor.PURPLE, name: t('day21.PURPLE') },
		{ id: ExerciseColor.LAVENDER, name: t('day21.LAVENDER') },
		{ id: ExerciseColor.BLUE, name: t('day21.BLUE') },
		{ id: ExerciseColor.CX, name: t('meditation.CX') },
		{ id: ExerciseColor.MTR, name: t('meditation.MTR') },
	];

	const exerciseTypeList = [
		{ id: 1, name: t('day21.audio') },
		{ id: 2, name: t('day21.video') },
	];
	const canList = [
		{ id: true, name: t('day21.can') },
		{ id: false, name: t('day21.cannot') },
	];
	const yesOrNoList = [
		{ id: true, name: t('day21.yes') },
		{ id: false, name: t('day21.no') },
	];

	const { data: details } = useGetMajorDetailQuery(majorId || '', {
		refetchOnMountOrArgChange: true,
	});
	const defaultPropertyFilter: MeditationSinglePageFilter = {
		pagination: { ...initialPagination, size: 50 },
		sortings: [],
		id: majorId || '',
	};

	const defaultValues: DefaultValues<MeditationSinglePageFilter> =
		defaultPropertyFilter;
	const methods = useForm<MeditationSinglePageFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as MeditationSinglePageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: MeditationSinglePageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetSingleListQuery(
			{ ...transformedSearchParams, id: majorId },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback(
		(response?: AdvanceSeriesSinglePageResDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	); // watch fields change

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.status == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('day21.deleteSeries')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('day21.isDeleteSeries')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteMajor(majorId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMeditationSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(`/exercise-manage/major-list`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await updateMajor({
			seriesPublishDTOList: [
				{ seriesId: majorId || '', published: type == 1 ? true : false },
			],
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateExerciseStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'serialNumber',
			headerName: t('marjorExercise.index'),
			flex: 10,
		},
		{
			field: 'titleZh',
			headerName: t('day21.exerciseTitleZh'),
			flex: 25,
		},
		{
			field: 'mediaType',
			headerName: t('day21.exerciseType'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.mediaType, exerciseTypeList)}</>
			),
		},
		{
			field: 'canCountTime',
			headerName: t('day21.canCountTime'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCountTime, canList)}</>
			),
		},
		{
			field: 'canCollectPoints',
			headerName: t('day21.canCollectPoints'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCollectPoints, canList)}</>
			),
		},
		{
			field: 'rewardPoints',
			headerName: t('achievementList.rewardPoints'),
			flex: 15,
			renderCell: (params: any) => params.row.rewardPoints || '-',
		},
		{
			field: 'rewardDesc',
			headerName: t('achievementList.rewardProp'),
			flex: 25,
			renderCell: (params: any) => params.row.rewardDesc || '-',
		},
		{
			field: 'status',
			headerName: t('day21.exerciseStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<Chip
					color={params.row.status == OnoffType.on ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.status, statusList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('day21.lastUpdateTime'),
			flex: 20,
		},
		{
			field: 'createdAt',
			headerName: t('day21.createTime'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/exercise-manage/major-list/info/${majorId}/detail/${params.row.singleId}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.marjorExerciseDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<SeriesReportDownloadButton
							seriesId={majorId}
							seriesTitle={details?.data.titleZh}
						/>
						{details?.data.status == OnoffType.on && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(0)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('day21.handleOff')}
							</Button>
						)}
						{details?.data.status == OnoffType.off && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(1)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('day21.handleOn')}
							</Button>
						)}
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('day21.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('day21.status')}
									valueElement={
										<Chip
											color={
												details?.data.status == OnoffType.on
													? 'chipPrimary'
													: 'default'
											}
											label={showLabel(details?.data.status, statusList)}
										/>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('day21.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('day21.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>{t('day21.text')}</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('day21.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('day21.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('day21.title')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('marjorExercise.author')}
													value={
														currentTab == 'zh'
															? details?.data.authorZh
															: currentTab == 'en'
															? details?.data.authorEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('marjorExercise.authorDesc')}
													value={
														currentTab == 'zh'
															? details?.data.authorDescZh
															: currentTab == 'en'
															? details?.data.authorDescEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('day21.color')}
													value={
														showLabel(details?.data.color, colorList) || '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('marjorExercise.password')}
													value={details?.data.password || '-'}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('day21.coverImage')}
													imageElement={
														<>
															{details?.data.coverImage && (
																<img
																	src={details?.data.coverImage.url}
																	width={358}
																	height={137}
																	style={{
																		objectFit: 'contain',
																	}}
																/>
															)}
														</>
													}
												/>
											</Grid>
										</Grid>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												{details?.data && (
													<FormControlLabel
														control={
															<Checkbox
																disabled
																checked={details?.data.hasContent}
															/>
														}
														label={t('marjorExercise.notesPage')}
													/>
												)}
											</Grid>
											{details?.data.hasContent && (
												<>
													{currentTab == 'zh' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('marjorExercise.notesPageText')}
																editorStateString={
																	details?.data.contentZh || '-'
																}
															/>
														</Grid>
													)}
													{currentTab == 'en' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('marjorExercise.notesPageText')}
																editorStateString={
																	details?.data.contentEn || '-'
																}
															/>
														</Grid>
													)}
												</>
											)}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>{t('day21.text')}</Typography>
										</Box>
										<MajorInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>

				<Card sx={{ marginTop: 3 }}>
					<CardContent>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h6'>{t('day21.exerciseList')}</Typography>
							<Stack direction={'row'} alignItems={'center'} spacing={3}>
								<SetSingleOrderDialog />
								<SetSingleStatusDialog />
								<Button
									variant='contained'
									style={{ alignItems: 'center' }}
									endIcon={<NoteAltOutlinedIcon />}
									onClick={() =>
										navigate(`/exercise-manage/major-list/info/${majorId}/add`)
									}
								>
									{t('button.add')}
								</Button>
							</Stack>
						</Box>
						<FormProvider {...methods}>
							<Box marginTop={2}>
								<AppDataGrid
									loading={!isGetPropertySummarySuccess}
									rows={transformResponse(propertySummary?.data?.items) ?? []}
									rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.singleId}
								/>
							</Box>
						</FormProvider>
					</CardContent>
				</Card>
			</Box>
		</>
	);
}
